/**
 * Naive UTC wall-clock for cruise itinerary dates/times.
 * Mirrors iOS NaiveWallClockDateParser: catalog digits are frozen in UTC so
 * "08:00" stays 08:00 when the phone timezone changes. Destination IANA ids
 * on city visits are metadata only — never used to convert these values.
 */

const YMD = /^(\d{4})-(\d{2})-(\d{2})/;
const HM = /^(\d{1,2}):(\d{2})/;
const ISO_HM = /T(\d{2}):(\d{2})/;

export function parseYmdParts(
  ymd: string
): { year: number; month: number; day: number } | null {
  const match = YMD.exec(ymd.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return { year, month, day };
}

/** YYYY-MM-DD from catalog/API/Device Sync strings without local TZ conversion. */
export function ymdFromUnknown(value: unknown): string | null {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const ymd = YMD.exec(raw);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) return null;
  const date = new Date(parsed);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** HH:mm from `13:00`, `8:30`, or `…T13:00:00Z` — civil digits, not local clock. */
export function hmFromUnknown(value: unknown): string | null {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const hm = HM.exec(raw);
  if (hm && !raw.includes('T')) {
    return `${hm[1].padStart(2, '0')}:${hm[2]}`;
  }
  const iso = ISO_HM.exec(raw);
  if (iso) return `${iso[1]}:${iso[2]}`;
  if (hm) return `${hm[1].padStart(2, '0')}:${hm[2]}`;
  return null;
}

export function addDaysYmd(ymd: string, days: number): string {
  const parts = parseYmdParts(ymd);
  if (!parts) return ymd;
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  date.setUTCDate(date.getUTCDate() + days);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function diffYmdDays(fromYmd: string, toYmd: string): number {
  const from = parseYmdParts(fromYmd);
  const to = parseYmdParts(toYmd);
  if (!from || !to) return 0;
  const a = Date.UTC(from.year, from.month - 1, from.day);
  const b = Date.UTC(to.year, to.month - 1, to.day);
  return Math.round((b - a) / 86_400_000);
}

export function utcDateFromYmd(ymd: string): Date | null {
  const parts = parseYmdParts(ymd);
  if (!parts) return null;
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
}

export function formatYmd(
  ymd: string,
  options: Intl.DateTimeFormatOptions
): string {
  const date = utcDateFromYmd(ymd);
  if (!date) return ymd;
  return date.toLocaleDateString(undefined, { ...options, timeZone: 'UTC' });
}

export function formatHm(hm: string): string {
  const parsed = hmFromUnknown(hm);
  if (!parsed) return hm;
  const [h, m] = parsed.split(':').map(Number);
  const date = new Date(Date.UTC(2000, 0, 1, h, m));
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** Device Sync / API wire form: civil digits with Z (iOS UTC wall-clock). */
export function isoUtcWallClock(ymd: string, hm?: string | null): string {
  const day = ymdFromUnknown(ymd) ?? ymd;
  const time = hmFromUnknown(hm ?? '') ?? '00:00';
  return `${day}T${time}:00Z`;
}

/** UTC civil YYYY-MM-DD for `now` (itinerary calendar “today”). */
export function utcTodayYmd(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Civil YYYY-MM-DD for `now` in an IANA zone (port “today” for hourly strip). */
export function ymdInTimeZone(now: Date, timeZone: string): string | null {
  const trimmed = timeZone.trim();
  if (!trimmed) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: trimmed,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const year = parts.find((p) => p.type === 'year')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    if (!year || !month || !day) return null;
    return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
}

/** Device-local civil YYYY-MM-DD (Country Info “has the trip started?”). */
export function deviceCivilYmd(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Mirrors iOS NaiveWallClockDateParser.hasTripStarted:
 * compare device civil YMD to wall-clock start YMD (not absolute midnight).
 */
export function hasTripStartedCivil(
  tripStartYmdOrIso: string | null | undefined,
  now: Date = new Date()
): boolean {
  const startYmd = ymdFromUnknown(tripStartYmdOrIso);
  if (!startYmd) return false;
  return deviceCivilYmd(now) >= startYmd;
}

export interface WallClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** Civil Y/M/D/H/M/S digits of an absolute instant in an IANA zone. */
export function partsInTimeZone(instant: Date, timeZone: string): WallClockParts | null {
  const trimmed = timeZone.trim();
  if (!trimmed || Number.isNaN(instant.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: trimmed,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(instant);
    const get = (type: Intl.DateTimeFormatPartTypes): number | null => {
      const value = parts.find((p) => p.type === type)?.value;
      if (value == null) return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };
    const year = get('year');
    const month = get('month');
    const day = get('day');
    const hour = get('hour');
    const minute = get('minute');
    const second = get('second');
    if (
      year == null ||
      month == null ||
      day == null ||
      hour == null ||
      minute == null ||
      second == null
    ) {
      return null;
    }
    return { year, month, day, hour, minute, second };
  } catch {
    return null;
  }
}

/**
 * Rewrites an absolute instant so its civil digits in `sourceTimeZone`
 * become the same digits in UTC (iOS wallClockDate(from:in:)).
 */
export function wallClockDateFromSourceTz(instant: Date, sourceTimeZone: string): Date {
  const parts = partsInTimeZone(instant, sourceTimeZone);
  if (!parts) return instant;
  return new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
  );
}

/** Same as wallClockDateFromSourceTz but returns Device Sync ISO `…Z`. */
export function wallClockIsoFromSourceTz(
  isoOrDate: string | Date,
  sourceTimeZone: string
): string | null {
  const instant =
    typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(instant.getTime())) return null;
  const rewritten = wallClockDateFromSourceTz(instant, sourceTimeZone);
  const y = rewritten.getUTCFullYear();
  const m = String(rewritten.getUTCMonth() + 1).padStart(2, '0');
  const d = String(rewritten.getUTCDate()).padStart(2, '0');
  const hh = String(rewritten.getUTCHours()).padStart(2, '0');
  const mm = String(rewritten.getUTCMinutes()).padStart(2, '0');
  const ss = String(rewritten.getUTCSeconds()).padStart(2, '0');
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}Z`;
}

/** Hour 0–23 from HH:mm or ISO wall-clock (UTC civil digits). */
export function wallClockHour(value: unknown): number | null {
  const hm = hmFromUnknown(value);
  if (!hm) return null;
  const hour = Number(hm.slice(0, 2));
  return Number.isFinite(hour) ? hour : null;
}
